import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import { MODELS } from '../../../../Apis/Models';
import { TABLES } from '../../../../Apis/Tables';
import { ACTION_MODEL } from '../../../../Helpers/ActionModel';
import { permissionObj } from '../../../../Helpers/permission';
import { MODEL_CONTEXT_ORDERS, TABS_FOODIE_ORDERS } from '../Model/Models';
import { useOrdersContextProvider } from './OrdersContextProvider';
import {VIEW_TYPE} from "../../../../enums/ViewType";


/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */


const FoodieMainOrdersApp = () => {

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    let { state } = location;
    const currentTab = state?.currentTab;
    const { paramsContext, setParamsContext } = useOrdersContextProvider(); //to know which tab you are on and manage button permissions
    const [moduleId, setModuleId] = useState(null);
    const [option, setOption] = useState(null);
    const [searchElement, setSearchElement] = useState('');
    const [search, setSearch] = useState(false);
    const [view, setView] = useState(VIEW_TYPE.LIST); //vista tarjetas o lista
    const [activeStep, setActiveStep] = useState(null);
    const [steps, setSteps] = useState([]);
    const [criteria, setCriteria] = useState();
    const [bodyForExport, setBodyForExport] = useState(null); // Data to export
    const [subView, setSubView] = useState(null); // Variable to manage subviews and permissions. When entering the module, it is necessary to initialize the variable (if applicable, used SUB_VIEW_MODEL).
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [extraMealType, setExtraMealType] = useState(null);
    const [simpleSearch, setSimpleSearch] = useState(false);

    /**
     * Hook to initialize the Context only if it is null or doesn't exist.
     */
    useLayoutEffect(() => {
        if (!paramsContext) {
            setParamsContext({ ...MODEL_CONTEXT_ORDERS });
        }
    }, []);

    /**
     * Hook to display the tabs if the user has the corresponding permissions.
     */
    useLayoutEffect(() => {
        let activeTab = null;
        const stepConfig = [
            { tab: "orders", perm: [permissionObj?.WEB_FOODIE_MENU_SELECTION_VIEW], index: TABS_FOODIE_ORDERS.ORDER.INDEX },
            { tab: "my_orders", perm: [permissionObj?.WEB_FOODIE_MENU_SELECTION_VIEW], index: TABS_FOODIE_ORDERS.ORDER.INDEX },
            { tab: "request_meals", perm: [permissionObj?.WEB_FOODIE_EXTRA_REQUEST_VIEW], index: TABS_FOODIE_ORDERS.REQUEST_MEALS.INDEX },
        ];

        const auxStep = stepConfig.reduce((acc, { tab, perm, index }) => {
            const hasPermission = DEV || perm.some(p => permission?.includes(p));
            if (hasPermission) {
                acc.push({ label: t(tab), index: index });
                if (activeTab === null) activeTab = index;
            }
            return acc;
        }, []);

        setSteps(auxStep);

        if (activeTab !== null && !paramsContext?.tabIndex && !currentTab) {
            handleStepChange(activeTab);
        }
    }, [t, permission]);

    /**
     * Hook to return to the current tab based on the index stored in the Context.
     */
    useEffect(() => {
        if (paramsContext?.tabIndex !== null && typeof paramsContext?.tabIndex !== "undefined") {
            handleStepChange(paramsContext?.tabIndex);
        }
    }, []);

    /**
     * Effect hook that updates the current step based on the active tab.
     *
     * This hook listens for changes in the URL location and triggers
     * `handleStepChange` whenever a valid `currentTab` value is present.
     */
    useLayoutEffect(() => {
        if(currentTab) handleStepChange(currentTab);
    }, [location]);

    /**
     * The index of the active tab is stored in the context, so that the user can return to the same tab in case the view is redirected.
     * @param step
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
        if (setParamsContext)
            setParamsContext(prev => ({
                ...prev, tabIndex: step
            }));
    };

    /**
     * Function for the create and/or delete action, where the modalDelete and modalCreate variables can be activated and/or a view redirection can be performed.
     * @param action
     * @param extraMealTypeOption
     */
    const handleCreateDelete = (action = ACTION_MODEL.CREATE, extraMealTypeOption) => {
        if (setParamsContext && paramsContext.tabIndex === TABS_FOODIE_ORDERS.ORDER.INDEX) {
            setParamsContext((prev) => ({
                ...prev,
                [action === ACTION_MODEL.CREATE ? 'modalCreate' : 'modalDelete']: true,
            }));
        }
        switch (paramsContext.tabIndex) {
            case TABS_FOODIE_ORDERS.ORDER.INDEX:
                break;

            case TABS_FOODIE_ORDERS.MY_ORDERS.INDEX:
                break;

            case TABS_FOODIE_ORDERS.REQUEST_MEALS.INDEX:
                if (setParamsContext) {
                    if (action === ACTION_MODEL.CREATE) {
                        setExtraMealType(extraMealTypeOption);
                        setShowRequestModal(true);
                    } else {
                        setParamsContext((prev) => ({
                            ...prev,
                            modalDelete: true,
                        }));
                    }
                }
                break;
            default:
                break;
        }
    };

    /**
     * Function to validate permissions for the create and delete buttons. It can also be applied to import, export, and search buttons.
     * If no permission is assigned or the tabIndex variable from the Context is null, the button is not displayed.
     * @param array list: List of permissions based on the active tab.
     * @returns {boolean}
     */
    const permissionButtons = (list) => {
        if (paramsContext?.tabIndex == null) {
            return false;
        }
        const permissionRequired = list[paramsContext?.tabIndex];

        if (!permissionRequired) {
            return false;
        }
        return DEV || permission?.includes(permissionRequired);
    };


    return {
        t,
        DEV,
        permission,
        navigate,
        searchElement, setSearchElement,
        search,
        view, setView,
        moduleId,
        setModuleId,
        option,
        setOption,
        activeStep, setActiveStep,
        steps,
        criteria, setCriteria,
        bodyForExport, setBodyForExport,
        paramsContext,
        handleStepChange,
        handleCreateDelete,
        permissionButtons,
        subView,
        setSubView,
        showRequestModal,
        setShowRequestModal,
        extraMealType,
        setExtraMealType,
        setSearch,
        simpleSearch,
        setSimpleSearch
    };

}

export default FoodieMainOrdersApp;
